import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Flex,
  Select,
  Input,
  Text,
  useColorModeValue,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Button,
  useToast,
  Skeleton,
  SimpleGrid,
  HStack,
  Heading,
  MenuList,
  MenuItem,
  Center,
  Spinner,
  Menu,
  MenuButton,
} from '@chakra-ui/react';

import {
  EditIcon,
  DragHandleIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import ApexChart from 'react-apexcharts';
import Card from 'components/card/Card';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Sidebar from 'components/sidebar/Sidebar';
import InterfaceSelect from 'components/select';
import { useDevices } from 'views/admin/context/DeviceContext';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import apiUrls from 'api/apiUrls';
import BreadCrumb from 'components/breadCrumb/BreadCrumb';
import { sortDevices } from 'views/hooks/sortDevices';
import axiosInstance from 'api/axiosInstance';

const ResponsiveGridLayout = WidthProvider(Responsive);
const chartTypes = ['line', 'bar', 'area', 'scatter', 'pie', 'radialBar'];

const HealthCharts = ({ inEditDevice, deviceName, onDeviceChange }) => {
  // const TOTALS_BY_TYPE = {
  //   1: {
  //     // MINI
  //     ram: 163840,
  //     disk: 1048576,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   2: {
  //     // GRIDX
  //     ram: 505413632,
  //     disk: 3650722201,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   3: {
  //     // MINI
  //     ram: 163840,
  //     disk: 1048576,
  //     cpu: 100,
  //     backup: 1073741824,
  //   },
  //   // Add more types as needed
  // };

  const [totalsConfig, setTotalsConfig] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);

  const getUsedPercent = (used, metric, device_type) => {
    if (!configLoaded) return 0;

    const totals = totalsConfig[String(device_type)] || totalsConfig['2'];

    if (!totals) return null;
    if (used == null) return 0;

    const total = totals[metric];

    if (!total || total <= 0) return null;
    if (used < 0) return 0;

    return (used / total) * 100;
  };
  const hoverBg = useColorModeValue('gray.200', 'navy.500');
  const focusBorderColor = useColorModeValue('navy.500', 'navy.300');
  const inputBg = useColorModeValue('white', 'navy.600');

  const [selectedDevice, setSelectedDevice] = useState(() => {
    // Initialize with deviceName or localStorage deviceId when in edit mode
    if (inEditDevice) {
      return deviceName || localStorage.getItem('deviceId') || null;
    }
    return null;
  });
  const currentDevice = localStorage.getItem('deviceId');
  const { id: paramDeviceName } = useParams();
  const getEditDeviceName = () => {
    if (inEditDevice) {
      // Try to get from localStorage, fallback to prop if not found
      return deviceName || localStorage.getItem('deviceId');
    }
    return selectedDevice;
  };
  const effectiveDeviceName = inEditDevice
    ? selectedDevice || deviceName || localStorage.getItem('deviceId')
    : selectedDevice;
  const toast = useToast();

  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'Device Health', path: '/modbus-configs' },
    { label: 'Health Chart', path: '' },
    { label: paramDeviceName, path: '' },
  ];

  const [telemetryData, setTelemetryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dragMode, setDragMode] = useState(false);
  const [hasManuallySelected, setHasManuallySelected] = useState(false);

  // Chart configuration states
  const [chartConfigs, setChartConfigs] = useState({});
  const [layout, setLayout] = useState([]);
  const [fullChartConfig, setFullChartConfig] = useState({
    layout: [],
    chartConfigs: {},
  });

  const formatLocalDateTime = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 60 * 60 * 1000);
    return {
      start: formatLocalDateTime(start),
      end: formatLocalDateTime(end),
    };
  });

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sstidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const sidebarWidth = collapsed ? '60px' : '240px';
  const panelBg = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('navy.200', 'navy.600');
  const textColor = useColorModeValue('navy.800', 'white');
  const [timeFilter, setTimeFilter] = useState('1h');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const toIST = (utcMs) => new Date(utcMs + 5.5 * 60 * 60 * 1000);

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [hasMoreDevices, setHasMoreDevices] = useState(true);
  const [devicePage, setDevicePage] = useState(0);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const searchTimeoutRef = useRef(null);
  const deviceListRef = useRef(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);

  console.log('Selected device:', selectedDeviceType);

  const metrics = React.useMemo(() => {
    if (
      selectedDeviceType === 1 ||
      selectedDeviceType === 3 ||
      selectedDeviceType === 0
    ) {
      return ['cpu', 'ram', 'disk'];
    }
    return ['cpu', 'ram', 'disk', 'backup_available'];
  }, [selectedDeviceType]);

  useEffect(() => {
    setLayout((prevLayout) => {
      // Remove layout items for metrics that no longer exist
      const filtered = prevLayout.filter((item) => metrics.includes(item.i));
      const missing = metrics.filter(
        (m) => !filtered.some((item) => item.i === m),
      );
      const appended = [
        ...filtered,
        ...missing.map((m, idx) => ({
          i: m,
          x: 0,
          y: filtered.length + idx,
          w: 1,
          h: 3,
          minW: 1,
          minH: 2,
        })),
      ];
      return appended.map((item, idx) => ({ ...item, y: idx }));
    });

    setChartConfigs((prevConfigs) => {
      // Remove configs for metrics that no longer exist
      const filtered = Object.fromEntries(
        Object.entries(prevConfigs).filter(([k]) => metrics.includes(k)),
      );
      // Add default configs for new metrics
      metrics.forEach((m) => {
        if (!filtered[m]) {
          filtered[m] = {
            type: 'area',
            color: getDefaultColor(m),
          };
        }
      });
      return filtered;
    });
  }, [metrics]);
  const deviceOptions = React.useMemo(() => {
    // Sort device names
    const sorted = sortDevices(devices.map((d) => d.name));
    // Convert to object: { name: name }
    return sorted.reduce((acc, name) => {
      acc[name] = name;
      return acc;
    }, {});
  }, [devices]);
  // Format metric names for display
  const formatMetricName = (metric) => {
    if (metric === 'backup_available') return 'BACKUP';
    return metric.toUpperCase();
  };

  const formatHealthDataForTelemetry = (apiData) => {
    if (!configLoaded) return [];
    if (!apiData) return [];

    const device_type = selectedDeviceType;
    // Get all timestamps from all metrics
    const allTimestamps = new Set();

    metrics.forEach((metric) => {
      if (apiData[metric] && Array.isArray(apiData[metric])) {
        apiData[metric].forEach((point) => {
          allTimestamps.add(point.timestamp);
        });
      }
    });

    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

    // Create data points for each timestamp
    const transformedData = sortedTimestamps.map((timestamp) => {
      const dataPoint = { timestamp: timestamp * 1000 };

      const device_type = selectedDeviceType;
      // console.log(
      //   `Processing timestamp ${timestamp} for device type ${device_type}`,
      // );
      const totals = totalsConfig[String(device_type)] || totalsConfig['2'];
      // RAM
      let ramAvailable = null;
      if (apiData.ram && Array.isArray(apiData.ram)) {
        const point = apiData.ram.find((p) => p.timestamp === timestamp);
        ramAvailable = point ? Number(point.value) : null;
      }
      const ramUsed = ramAvailable != null ? totals.ram - ramAvailable : null;
      dataPoint.ram = getUsedPercent(ramUsed, 'ram', device_type);

      // DISK
      let diskAvailable = null;
      if (apiData.disk && Array.isArray(apiData.disk)) {
        const point = apiData.disk.find((p) => p.timestamp === timestamp);
        diskAvailable = point ? Number(point.value) : null;
      }
      const diskUsed =
        diskAvailable != null ? totals.disk - diskAvailable : null;
      dataPoint.disk = getUsedPercent(diskUsed, 'disk', device_type);

      // BACKUP
      let backupAvailable = null;
      if (apiData.backup_available && Array.isArray(apiData.backup_available)) {
        const point = apiData.backup_available.find(
          (p) => p.timestamp === timestamp,
        );
        backupAvailable = point ? Number(point.value) : null;
      }
      const backupUsed =
        backupAvailable != null ? totals.backup - backupAvailable : null;
      dataPoint.backup_available = getUsedPercent(
        backupUsed,
        'backup',
        device_type,
      );

      // CPU (assume value is already percent used)
      let cpuValue = null;
      if (apiData.cpu && Array.isArray(apiData.cpu)) {
        const point = apiData.cpu.find((p) => p.timestamp === timestamp);
        cpuValue = point ? Number(point.value) : null;
      }
      dataPoint.cpu = getUsedPercent(cpuValue, 'cpu', device_type);

      return dataPoint;
    });

    return transformedData;
  };
  // Load saved chart config from localStorage
  useEffect(() => {
    const loadSavedConfig = () => {
      try {
        const savedConfig = localStorage.getItem(
          `health_charts_config_${effectiveDeviceName}`,
        );
        if (savedConfig) {
          const config = JSON.parse(savedConfig);
          setFullChartConfig(config);

          // Initialize chart configs and layout for current metrics
          const initialChartConfigs = {};
          const initialLayout = [];

          metrics.forEach((param, index) => {
            const chartKey = param;
            const savedChartConfig = config.chartConfigs?.[chartKey] || {
              type: 'area',
              color: getDefaultColor(param),
            };
            initialChartConfigs[chartKey] = savedChartConfig;

            const layoutItem = config.layout?.find((l) => l.i === chartKey) || {
              i: chartKey,
              x: 0,
              y: Math.floor(index / 3) * 3,
              w: 1,
              h: 3,
              minW: 1,
              minH: 2,
            };
            initialLayout.push(layoutItem);
          });

          setChartConfigs(initialChartConfigs);
          setLayout(initialLayout);
        } else {
          // Initialize with default config
          const initialChartConfigs = {};
          const initialLayout = [];

          metrics.forEach((param, index) => {
            const chartKey = param;
            initialChartConfigs[chartKey] = {
              type: 'area',
              color: getDefaultColor(param),
            };

            initialLayout.push({
              i: chartKey,
              x: 0,
              y: Math.floor(index / 3) * 3,
              w: 1,
              h: 3,
              minW: 1,
              minH: 2,
            });
          });

          setChartConfigs(initialChartConfigs);
          setLayout(initialLayout);
        }
      } catch (err) {
        console.error('Failed to load chart config:', err);
      }
    };

    if (effectiveDeviceName) {
      loadSavedConfig();
    }
  }, [effectiveDeviceName]);

  const getDefaultColor = (metric) => {
    const colors = {
      cpu: '#7a31ce',
      ram: '#2c7da0',
      disk: '#2e8b57',
      backup: '#d2691e',
    };
    return colors[metric] || '#7a31ce';
  };

  const updateChartConfig = (chartKey, field, value) => {
    setChartConfigs((prev) => ({
      ...prev,
      [chartKey]: {
        ...prev[chartKey],
        [field]: value,
      },
    }));
  };

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const saveChartConfig = () => {
    try {
      const updatedFullConfig = {
        layout: layout,
        chartConfigs: chartConfigs,
      };

      localStorage.setItem(
        `health_charts_config_${selectedDevice}`,
        JSON.stringify(updatedFullConfig),
      );
      setFullChartConfig(updatedFullConfig);
      toast({
        title: 'Chart layout saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Save failed:', err);
      toast({
        title: 'Save failed',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  const getDeviceType = (deviceName) => {
    const device = (devices || []).find((d) => d.name === deviceName);
    // console.log('Device found for type:', device);
    return device ? device.device_type : 2;
  };

  const getMaxValue = (device_type, metric) => {
    const totals = totalsConfig[String(device_type)] || totalsConfig['2'];

    if (!totals) return 100;

    if (metric === 'backup_available') return totals.backup;

    return totals[metric] ?? 100;
  };

  const fetchDeviceAttributes = useCallback(async (deviceName) => {
    const keys =
      'warranty_date,location,app_version,device_type,patch_v,group_id,history,modbus_config_name,patch_name,config_status,patch_status,modbus_v';

    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      if (attrMap.group_id) {
        localStorage.setItem('groupID', attrMap.group_id);
      }

      return {
        warranty: attrMap.warranty_date ?? null,
        location: attrMap.location ?? '',
        app_version: attrMap.app_version ?? '-',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? '-',
        group_id: attrMap.group_id ?? null,
        history: attrMap.history ?? [],
        modbus_config_name: attrMap.modbus_config_name ?? '-',
        patch_name: attrMap.patch_name ?? '-',
        config_status: attrMap.config_status ?? '-',
        patch_status: attrMap.patch_status ?? '-',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('Error fetching attributes:', error);
      return null;
    }
  }, []);

  const fetchAllDevices = useCallback(
    async (page = 0, search = '', { ensureDevice } = {}) => {
      setLoadingDevices(true);

      try {
        const resp = await axiosInstance.get(apiUrls.allDevices(search, page));
        const devicesData = resp.data.data || [];
        const hasNext = resp.data.hasNext || false;

        const attrResults = await Promise.all(
          devicesData.map(async (d) => {
            const attrs = await fetchDeviceAttributes(d.name);
            return attrs ? d : null;
          }),
        );

        const validDevices = attrResults.filter(Boolean);

        if (page === 0) {
          setDevices(validDevices);
        } else {
          setDevices((prev) => [...prev, ...validDevices]);
        }

        setHasMoreDevices(hasNext);

        if (hasNext) {
          setDevicePage(page);
        }

        if (
          ensureDevice &&
          !validDevices.find((d) => d.name === ensureDevice)
        ) {
          const searchResp = await axiosInstance.get(
            apiUrls.allDevices(ensureDevice, 0),
          );
          const searchDevices = searchResp.data.data || [];
          const found = searchDevices.find((d) => d.name === ensureDevice);
          if (found) {
            const foundAttrs = await fetchDeviceAttributes(found.name);
            if (foundAttrs) {
              setDevices((prev) => [...prev, found]);
            }
          }
        }
      } catch (err) {
        toast({
          title: 'Error fetching device list',
          description: err.message || 'Something went wrong.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingDevices(false);
      }
    },
    [toast, fetchDeviceAttributes],
  );

  const handleDeviceSearch = useCallback(
    (search) => {
      setDeviceSearch(search);
      fetchAllDevices(0, search);
    },
    [fetchAllDevices],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      handleDeviceSearch(value);
    }, 400);
  };

  const fetchHealth = async (deviceName, start, end) => {
    try {
      setLoading(true);
      const startISO = new Date(start).toISOString();
      const endISO = new Date(end).toISOString();

      const keys = 'cpu,ram,disk,backup_available';
      const step = '10s';

      const url = apiUrls.fetchMetricsData(
        deviceName,
        startISO,
        endISO,
        step,
        keys,
      );

      const resp = await axios.get(url);

      // Transform the data for the chart component
      const transformedData = formatHealthDataForTelemetry(resp.data);
      setTelemetryData(transformedData);
    } catch (err) {
      console.error('❌ API ERROR:', err);
      setTelemetryData([]);
      toast({
        title: 'Error fetching data',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize selected device from URL params and localStorag
  useEffect(() => {
    if (!devices.length) return; // wait for API

    if (hasManuallySelected) return;

    if (paramDeviceName) {
      const exists = devices.some((d) => d.name === paramDeviceName);
      if (exists) {
        setSelectedDevice(paramDeviceName);
        return;
      }
    }

    // fallback → first device
    setSelectedDevice(devices[0].name);
  }, [devices, paramDeviceName, hasManuallySelected]);

  // Reset manual selection flag when URL param changes
  useEffect(() => {
    if (
      paramDeviceName &&
      paramDeviceName !== selectedDevice &&
      !hasManuallySelected
    ) {
      // If URL param changed and it's different from current selection,
      // reset manual selection flag to allow initialization to run
      setHasManuallySelected(false);
    }
  }, [paramDeviceName, selectedDevice, hasManuallySelected]);

  useEffect(() => {
    if (!effectiveDeviceName || !configLoaded || selectedDeviceType === null)
      return;
    if (!dateRange.start || !dateRange.end) return;
    fetchHealth(effectiveDeviceName, dateRange.start, dateRange.end);
  }, [effectiveDeviceName, dateRange, configLoaded, selectedDeviceType]);

  // Handlers
  const handleDeviceChange = (val) => {
    if (inEditDevice && onDeviceChange) {
      onDeviceChange(val);
    } else {
      setSelectedDevice(val);
      setHasManuallySelected(true);
    }
  };
  const handleDateChange = (type, value) => {
    // console.log(`📅 Date changed (${type}):`, value);
    setDateRange((prev) => ({ ...prev, [type]: value }));
  };

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (timeFilter === 'custom') {
      if (customRange.start && customRange.end) {
        setDateRange({
          start: customRange.start,
          end: customRange.end,
        });
      }
    } else {
      // Parse the time filter (e.g., "1h", "2d")
      const now = new Date();
      let start;
      const num = parseInt(timeFilter, 10);
      if (timeFilter.endsWith('m')) {
        start = new Date(now.getTime() - num * 60 * 1000);
      } else if (timeFilter.endsWith('h')) {
        start = new Date(now.getTime() - num * 60 * 60 * 1000);
      } else if (timeFilter.endsWith('d')) {
        start = new Date(now.getTime() - num * 24 * 60 * 60 * 1000);
      } else {
        start = new Date(now.getTime() - 60 * 60 * 1000); // default 1h
      }
      setDateRange({
        start: formatLocalDateTime(start),
        end: formatLocalDateTime(now),
      });
    }
  }, [timeFilter, customRange]);

  // Chart series generation
  const getChartSeries = (key, type) => {
    const seriesData = telemetryData
      .map((item) => {
        const val = item[key];
        if (val !== undefined && val !== null && !isNaN(val)) {
          return [new Date(item.timestamp).getTime(), parseFloat(val)];
        }
        return null;
      })
      .filter((v) => v !== null && v !== undefined);
    switch (type) {
      case 'pie':
      case 'radialBar': {
        const lastValidItem = telemetryData
          .slice()
          .reverse()
          .find(
            (item) =>
              item[key] !== undefined &&
              item[key] !== null &&
              !isNaN(item[key]),
          );
        const value = lastValidItem ? parseFloat(lastValidItem[key]) : 0;
        return [{ name: formatMetricName(key), data: [value] }];
      }
      case 'scatter':
        return [
          {
            name: formatMetricName(key),
            data: seriesData.map(([x, y]) => ({ x, y })),
          },
        ];
      default:
        return [
          {
            name: formatMetricName(key),
            data: seriesData,
          },
        ];
    }
  };

  // Chart options generation
  const getChartOptions = (label, type, color) => {
    const showGrid = editMode && type === 'line';

    const baseOptions = {
      chart: {
        type,
        zoom: { enabled: true },
        toolbar: { show: true },
        background: panelBg,
        fontFamily: 'Inter, sans-serif',
        animations: { enabled: false },
      },
      colors: [color],
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      legend: {
        position: 'top',
        labels: { colors: textColor, fontWeight: '600' },
      },
      grid: {
        borderColor: borderColor,
        strokeDashArray: showGrid ? 4 : 0,
        row: {
          colors: ['transparent'],
          opacity: showGrid ? 0.3 : 0,
        },
        show: showGrid,
      },
      tooltip: {
        x: {
          formatter: (value) =>
            new Date(value).toLocaleString(undefined, {
              hour12: false,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
        },
        y: {
          formatter: (value) => `${value.toFixed(2)}%`,
        },
        theme: textColor === 'white' ? 'dark' : 'light',
      },
      title: {
        text: `${formatMetricName(label)} `,
        align: 'left',
        style: {
          color: textColor,
          fontSize: '16px',
          fontWeight: 600,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };

    const datetimeAxis = {
      type: 'datetime',
      labels: {
        style: { colors: textColor, fontSize: '11px' },
        rotate: -45,
        formatter: (value) =>
          new Date(value).toLocaleString(undefined, {
            hour12: false,
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      },
      axisBorder: { color: borderColor },
      axisTicks: { color: borderColor },
    };

    if (type === 'area') {
      return {
        ...baseOptions,
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.8,
            opacityTo: 0,
            stops: [0, 100],
            colorStops: [
              { offset: 0, color: color, opacity: 0.8 },
              { offset: 100, color: color, opacity: 0 },
            ],
          },
        },
        xaxis: datetimeAxis,
        yaxis: {
          min: 0,
          max: 100,
          labels: {
            style: { colors: textColor, fontSize: '12px' },
            formatter: (value) => `${value.toFixed(1)}%`,
          },
          axisBorder: { color: borderColor },
          axisTicks: { color: borderColor },
          title: {
            text: 'Usage (%)',
            style: { color: textColor, fontSize: '12px' },
          },
        },
      };
    }

    if (['line', 'bar', 'scatter'].includes(type)) {
      return {
        ...baseOptions,
        xaxis: datetimeAxis,
        yaxis: {
          labels: {
            style: { colors: textColor, fontSize: '12px' },
            formatter: (value) => `${value.toFixed(1)}%`,
          },
          axisBorder: { color: borderColor },
          axisTicks: { color: borderColor },
          title: {
            text: 'Usage (%)',
            style: { color: textColor, fontSize: '12px' },
          },
        },
      };
    }

    if (type === 'pie') {
      return {
        ...baseOptions,
        labels: [formatMetricName(label)],
        legend: { position: 'bottom' },
      };
    }

    if (type === 'radialBar') {
      return {
        ...baseOptions,
        plotOptions: {
          radialBar: {
            dataLabels: {
              name: { show: true, fontSize: '16px' },
              value: {
                show: true,
                fontSize: '14px',
                formatter: (val) => `${val.toFixed(1)}%`,
              },
            },
            track: { background: borderColor },
          },
        },
      };
    }

    return baseOptions;
  };

  useEffect(() => {
    fetchAllDevices();
  }, []);

  useEffect(() => {
    if (deviceListRef.current && hasMoreDevices && !loadingDevices) {
      const observer = new window.IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            fetchAllDevices(devicePage + 1, deviceSearch);
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(deviceListRef.current);
      return () => observer.disconnect();
    }
  }, [
    hasMoreDevices,
    loadingDevices,
    devicePage,
    deviceSearch,
    fetchAllDevices,
  ]);
  useEffect(() => {
    const savedDevice = localStorage.getItem('deviceId');
    if (savedDevice) {
      fetchAllDevices(0, '', { ensureDevice: savedDevice });
    } else {
      fetchAllDevices(0);
    }
  }, []);
  useEffect(() => {
    if (inEditDevice) {
      const initialDevice = deviceName || localStorage.getItem('deviceId');
      if (initialDevice && initialDevice !== selectedDevice) {
        setSelectedDevice(initialDevice);
      }
    }
  }, [inEditDevice, deviceName, selectedDevice]);

  useEffect(() => {
    if (effectiveDeviceName) {
      fetchDeviceAttributes(effectiveDeviceName).then((attrs) => {
        setSelectedDeviceType(
          attrs && attrs.device_type !== undefined && attrs.device_type !== null
            ? Number(attrs.device_type)
            : null,
        );
      });
    }
  }, [effectiveDeviceName, fetchDeviceAttributes]);

  useEffect(() => {
    fetch(`/devicetotals.json?ts=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        setTotalsConfig(data);
        setConfigLoaded(true);
        // console.log('Config loaded (charts):', data);
      })
      .catch((err) => {
        console.error('Failed to load config:', err);
        setConfigLoaded(true);
      });
  }, []);

  return (
    <Box
      ml={inEditDevice ? 0 : sidebarWidth}
      transition="margin-left 0.3s ease"
      p={inEditDevice ? 2 : 6}
    >
      {!inEditDevice && <BreadCrumb items={breadcrumbItems} />}

      {!inEditDevice && (
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      )}

      <Flex justify="space-between" align="center" width="100%" mb={6}>
        <Heading size="lg">Health Chart </Heading>

        <Flex align="center" gap={6}>
          <Flex align="center" gap={2}>
            <Flex align="center" gap={2}>
              <Text fontWeight="600" whiteSpace="nowrap">
                Select Device:
              </Text>

              <Menu>
                {({ isOpen }) => (
                  <>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      size="sm"
                      width="180px"
                      textAlign="left"
                      bg={inputBg}
                      borderRadius="lg"
                      border="2px"
                      borderColor={borderColor}
                      _hover={{ bg: hoverBg }}
                      _focus={{
                        outline: 'none',
                        borderColor: focusBorderColor,
                        boxShadow: `0 0 0 1px ${focusBorderColor}`,
                      }}
                    >
                      {effectiveDeviceName || 'Select Device'}{' '}
                      {/* Use effectiveDeviceName here */}
                    </MenuButton>
                    <MenuList
                      borderRadius="lg"
                      fontSize="14px"
                      p="2"
                      bg={inputBg}
                      color={textColor}
                      maxH="300px"
                      zIndex={100}
                      overflowY="auto"
                      boxShadow={
                        'rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px, rgba(17, 17, 26, 0.1) 0px 24px 80px;'
                      }
                    >
                      <Box p={2}>
                        <Input
                          placeholder="Search..."
                          value={searchValue}
                          onChange={handleSearchChange}
                          size="sm"
                          borderRadius="16px"
                        />
                      </Box>
                      {Object.entries(deviceOptions).map(([label]) => (
                        <MenuItem
                          key={label}
                          onClick={() => handleDeviceChange(label)}
                          fontWeight={500}
                          my={2}
                        >
                          {label}
                        </MenuItem>
                      ))}
                      <div ref={deviceListRef} style={{ height: 1 }} />
                      {loadingDevices && (
                        <Center p={2}>
                          <Spinner size="sm" />
                        </Center>
                      )}
                    </MenuList>
                  </>
                )}
              </Menu>
            </Flex>
          </Flex>

          <Flex align="center" gap={2}>
            <Text fontWeight="600" whiteSpace="nowrap">
              Date Range:
            </Text>
            <HStack spacing={2}>
              <InterfaceSelect
                width="140px"
                border="2px solid lightgray"
                value={timeFilter}
                options={[
                  '5m',
                  '10m',
                  '15m',
                  '30m',
                  '1h',
                  '2h',
                  '3h',
                  '4h',
                  '6h',
                  '12h',
                  '1d',
                  'custom',
                ]}
                onChange={(val) => setTimeFilter(val)}
              />
              {timeFilter === 'custom' && (
                <HStack
                  spacing={3}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="gray.50"
                  align="center"
                  flexWrap="wrap"
                >
                  <Input
                    type="datetime-local"
                    size="sm"
                    value={customRange.start}
                    maxW="200px"
                    bg="white"
                    borderRadius="md"
                    onChange={(e) =>
                      setCustomRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                  />

                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    to
                  </Text>

                  <Input
                    type="datetime-local"
                    size="sm"
                    value={customRange.end}
                    maxW="200px"
                    bg="white"
                    borderRadius="md"
                    onChange={(e) =>
                      setCustomRange((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                  />
                </HStack>
              )}
            </HStack>
          </Flex>
        </Flex>
      </Flex>

      {/* Chart Controls */}
      {/* <Flex justify="space-between" align="center" mb={4}>
        <Flex gap={2}>
          <Tooltip label={editMode ? 'Disable Edit Mode' : 'Enable Edit Mode'}>
            <IconButton
              aria-label="Toggle edit mode"
              icon={<EditIcon />}
              onClick={() => setEditMode((prev) => !prev)}
              colorScheme={editMode ? 'purple' : 'gray'}
              variant={editMode ? 'solid' : 'outline'}
              size="sm"
            />
          </Tooltip>

          <Tooltip
            label={dragMode ? 'Disable Drag & Resize' : 'Enable Drag & Resize'}
          >
            <IconButton
              aria-label="Toggle drag/resize"
              icon={<DragHandleIcon />}
              onClick={() => setDragMode((prev) => !prev)}
              colorScheme={dragMode ? 'purple' : 'gray'}
              variant={dragMode ? 'solid' : 'outline'}
              size="sm"
            />
          </Tooltip>
        </Flex>

        <Button
          leftIcon={<CheckIcon />}
          size="sm"
          colorScheme="purple"
          onClick={saveChartConfig}
        >
          Save Layout
        </Button>
      </Flex> */}

      {/* Charts Grid */}
      {loading ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton key={idx} height="350px" borderRadius="md" />
          ))}
        </SimpleGrid>
      ) : telemetryData.length === 0 ? (
        <Text color={textColor} textAlign="center" mt={6}>
          No data to display. Please select a device and date range.
        </Text>
      ) : (
        <ResponsiveGridLayout
          className="layout"
          style={{ width: '100%', maxWidth: '2000px', marginRight: '20px' }} // Add this
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 1, md: 1, sm: 1, xs: 1, xxs: 1 }}
          rowHeight={150}
          margin={[24, 24]}
          isDraggable={dragMode}
          isResizable={dragMode}
          compactType={null}
          preventCollision={false}
          layout={layout}
          onLayoutChange={onLayoutChange}
        >
          {metrics.map((param) => {
            const chartKey = param;
            const config = chartConfigs[chartKey] || {
              type: 'area',
              color: getDefaultColor(param),
            };
            const { type, color } = config;

            const layoutItem = layout.find((l) => l.i === chartKey) || {
              i: chartKey,
              x: 0,
              y: Infinity,
              w: 1,
              h: 3,
            };

            return (
              <div
                key={chartKey}
                data-grid={layoutItem}
                style={{ marginLeft: '-20px' }}
              >
                <Card
                  bg={panelBg}
                  transition="all 0.3s"
                  _hover={{ boxShadow: editMode ? 'lg' : 'none' }}
                  height="100%"
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  p={4}
                >
                  {/* <Stack mb={4} spacing={3} flexShrink={0}>
                    <Flex
                      justify="space-between"
                      align="center"
                      gap={4}
                      flexWrap="wrap"
                    >
                      <Box flex="1" minW="140px">
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          mb={1}
                          color={textColor}
                        >
                          Chart Type
                        </Text>
                        <Select
                          value={type}
                          onChange={(e) =>
                            updateChartConfig(chartKey, 'type', e.target.value)
                          }
                          size="sm"
                          borderColor={borderColor}
                          bg={panelBg}
                          isDisabled={!editMode}
                        >
                          {chartTypes.map((t) => (
                            <option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                          ))}
                        </Select>
                      </Box>

                      <Box flex="1" textAlign="end" minW="140px">
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          mb={1}
                          color={textColor}
                        >
                          Chart Color
                        </Text>
                        <Input
                          type="color"
                          value={color}
                          onChange={(e) =>
                            updateChartConfig(chartKey, 'color', e.target.value)
                          }
                          size="sm"
                          w="80px"
                          p={1}
                          bg="transparent"
                          isDisabled={!editMode}
                        />
                      </Box>
                    </Flex>
                    <Divider borderColor={borderColor} />
                  </Stack> */}

                  <Box flex="1" minHeight={0}>
                    <ApexChart
                      options={getChartOptions(param, type, color)}
                      series={getChartSeries(param, type)}
                      type={type}
                      height="100%"
                      width="100%"
                    />
                  </Box>
                </Card>
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
      <Flex
        as="footer"
        position="fixed"
        left={0}
        bottom="0"
        width="100%"
        bg={useColorModeValue('gray.100', 'navy.800')}
        color={useColorModeValue('gray.600', 'gray.200')}
        py={2}
        px={4}
        align="center"
        justify="space-between"
        zIndex={20} // higher than sidebar
        borderTop="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
      >
        <Text fontSize="sm" fontWeight="medium">
          Version 1.0.0
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          UGX Systems
        </Text>
      </Flex>
    </Box>
  );
};

export default HealthCharts;
